import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { JobStatusEnum, jobStatusToLabel } from "@/lib/types/job";
import { supabase } from "@/lib/supabase/supabaseClient";
import {
	JobWithShare,
	SupabaseShareEntityViewEnum,
} from "@/lib/supabase/supabaseTypes";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { deDE } from "@mui/x-data-grid/locales";
import { Tooltip, IconButton } from "@mui/material";
import { MessageCircle, Search, PanelRightOpen } from "lucide-react";
import { TEMPLATE_CODE } from "@/dentlab/src/store/Central/utils";
import { Logger } from "@/lib/logger/Logger";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { useJobSheet } from "@/dentlab/src/store/Sheets/sheet-hooks/useJobSheet";
import { JobDocumentTags } from "../../job-page/job-document/job-document-tag.component";
import {
	getStatusColor,
	getStatusIcon,
} from "@/dentlab/src/lib/utils/job-utils";

export const DashboardJobs: React.FC = () => {
	const { clientsLookup } = useCentralStore((state) => ({
		clientsLookup: state.clientsLookup,
	}));
	const { patientName, patientsLookup } = usePatientStore((state) => ({
		patientName: state.patientName,
		patientsLookup: state.patientsLookup,
	}));

	const { shareFile, unshareFile, acceptJobRequest, denyJobRequest } =
		useJobStore((state) => ({
			shareFile: state.shareFile,
			unshareFile: state.unshareFile,
			acceptJobRequest: state.acceptJobRequest,
			denyJobRequest: state.denyJobRequest,
		}));

	const navigate = useNavigate();
	const [jobs, setJobs] = useState<JobWithShare[]>([]);
	const [loading, setLoading] = useState<boolean>(true);

	const { openJobSheet } = useJobSheet();

	const handleOpenJobDetail = (job: JobWithShare) => {
		openJobSheet({
			job: job,
			onShareFile: shareFile,
			onUnshareFile: unshareFile,
			onAcceptJobRequest: acceptJobRequest,
			onDenyJobRequest: denyJobRequest,
		});
	};

	const columns: GridColDef[] = [
		{
			field: "actions",
			headerName: "",
			width: 90,
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<div className="flex items-center gap-1">
					<Tooltip title="Auftrag in Seitenleiste öffnen">
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								handleOpenJobDetail(params.row);
							}}
							sx={{ padding: "2px" }}
						>
							<PanelRightOpen className="h-3 w-3" />
						</IconButton>
					</Tooltip>
					{params.row.shared_with && (
						<Tooltip title="Der Auftrag ist mit dem Auftraggeber geteilt">
							<MessageCircle className="h-3 w-3 text-blue-500" />
						</Tooltip>
					)}
				</div>
			),
		},
		{
			field: "status",
			headerName: "Status",
			width: 200,
			renderCell: (params) => (
				<div className="flex items-center gap-1">
					<span className={cn(getStatusColor(params.value))}>
						{getStatusIcon(params.value)}
					</span>
					<span className="text-xs">
						{jobStatusToLabel[params.value as JobStatusEnum]}
					</span>
				</div>
			),
		},
		{
			field: "job_document_types",
			headerName: "Dokumente",
			width: 120,
			renderCell: (params) => <JobDocumentTags job={params.row} />,
		},
		{
			field: "modified_at",
			headerName: "Geändert",
			width: 85,
			valueGetter: (params) =>
				new Date(params.value).toLocaleDateString("de-DE", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
				}),
			renderCell: (params) => (
				<div className="text-xs">{params.value}</div>
			),
		},
		{
			field: "created_at",
			headerName: "Erstellt",
			width: 85,
			valueGetter: (params) =>
				new Date(params.value).toLocaleDateString("de-DE", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
				}),
			renderCell: (params) => (
				<div className="text-xs">{params.value}</div>
			),
		},
		{
			field: "code",
			headerName: "Code",
			width: 110,
			renderCell: (params) => (
				<div className="font-mono text-xs">{params.value}</div>
			),
		},
		{
			field: "title",
			headerName: "Titel",
			width: 250,
			renderCell: (params) => (
				<div className="text-xs">{params.value}</div>
			),
		},
		{
			field: "patient_name",
			headerName: "Patient",
			width: 180,
			valueGetter: (params) => {
				if (!patientsLookup || !params.row.patient_id) return "";
				return patientName(patientsLookup[params.row.patient_id]);
			},
			renderCell: (params) => (
				<div className="text-xs">{params.value}</div>
			),
		},
		{
			field: "client_name",
			headerName: "Auftraggeber",
			width: 180,
			valueGetter: (params) => {
				const client = clientsLookup[params.row.client_id];
				if (!client) return "";
				return `${client.first_name ?? ""} ${
					client.last_name ?? ""
				}`.trim();
			},
			renderCell: (params) => (
				<div className="text-xs">{params.value}</div>
			),
		},
	];

	const fetchJobs = async () => {
		setLoading(true);
		const organizationId = useCentralStore.getState().organization?.id;

		if (!organizationId) {
			Logger.error("Organization Id not present");
			setLoading(false);
			return;
		}

		try {
			// Build the query
			const query = supabase
				.from(SupabaseShareEntityViewEnum.JOBS_WITH_SHARES)
				.select("*")
				.eq("organization_id", organizationId)
				.neq("code", TEMPLATE_CODE)
				.order("modified_at", { ascending: false })
				.limit(100);

			const { data, error } = await query;

			if (error) {
				Logger.error(error, {}, "Error occurred fetching jobs");
				setLoading(false);
				return;
			}

			setJobs(data);
		} catch (error) {
			Logger.error(error, {}, "Error in fetchJobs");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchJobs();
	}, []);

	return (
		<div className="px-2 h-full w-full">
			<div style={{ height: "calc(100vh - 100px)", width: "100%" }}>
				<DataGrid
					rows={jobs}
					columns={columns}
					loading={loading}
					localeText={
						deDE.components.MuiDataGrid.defaultProps.localeText
					}
					disableRowSelectionOnClick
					onRowClick={(params) =>
						navigate(
							`/client/${params.row.client_id}/job/${params.row.id}`
						)
					}
					rowHeight={32}
					initialState={{
						pagination: {
							paginationModel: { page: 0, pageSize: 50 },
						},
					}}
					pageSizeOptions={[25, 50, 100]}
					slots={{
						noRowsOverlay: () => (
							<div className="flex flex-col items-center justify-center h-full">
								<Search className="h-6 w-6 text-gray-300 mb-1" />
								<p className="text-gray-500 text-xs">
									Keine Aufträge gefunden
								</p>
							</div>
						),
					}}
					sx={{
						border: "none",
						"& .MuiDataGrid-main": {
							fontSize: "0.75rem", // text-xs equivalent
						},
						"& .MuiDataGrid-cell": {
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						},
						"& .MuiDataGrid-columnSeparator": {
							display: "none",
						},
						"& .MuiDataGrid-cell:focus": {
							outline: "none",
						},
						"& .MuiDataGrid-columnHeader:focus": {
							outline: "none",
						},
						"& .MuiDataGrid-cell:focus-within": {
							outline: "none",
						},
						"& .MuiDataGrid-row": {
							borderBottom: "1px solid rgba(224, 224, 224, 0.3)",
							cursor: "pointer",
						},
						"& .MuiDataGrid-row:hover": {
							backgroundColor: "rgba(0, 0, 0, 0.02)",
						},
						"& .MuiDataGrid-columnHeaders": {
							borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
						},
						"& .MuiDataGrid-iconButtonContainer": {
							visibility: "visible",
						},
						"& .MuiDataGrid-sortIcon": {
							opacity: 0.5,
							fontSize: "0.75rem",
						},
						"& .MuiDataGrid-footerContainer": {
							borderTop: "1px solid rgba(224, 224, 224, 0.5)",
							minHeight: "36px",
						},
					}}
				/>
			</div>
		</div>
	);
};
